import React, { Dispatch, SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Close } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography } from '@mui/material';

import { AddSecretAndVariableForm } from '../../utils/models/secrets-and-variables';


interface SecretAndVariableModalProps {
    open: boolean;
    formData: AddSecretAndVariableForm;
    headingTitle: string;
    initializedSecrets?: string[];
    setOpen: Dispatch<SetStateAction<boolean>>;
    handleDataSubmit: (data: AddSecretAndVariableForm) => void;
}

const SecretAndVariableModal: React.FC<SecretAndVariableModalProps> = ({
    open,
    formData,
    headingTitle,
    initializedSecrets = [],
    setOpen,
    handleDataSubmit,
}) => {

    const initialFormData: AddSecretAndVariableForm = {
        name: formData.name,
        value: formData.value,
    }

    const { control, handleSubmit, reset, formState: { errors } } = useForm<AddSecretAndVariableForm>({
        defaultValues: initialFormData
    });

    const handleClose = () => {
        setOpen(false);
    }

    const handleFormSubmit = (form_data: AddSecretAndVariableForm) => {
        handleDataSubmit(form_data)
        reset();
        setOpen(false);
    }

    return (
        <Dialog
            open={open}
            component='form'
            onClose={handleClose}
            onSubmit={handleSubmit(handleFormSubmit)}
            sx={{
                '& .MuiDialog-paperWidthSm': {
                    width: '750px',
                    maxWidth: '750px',
                    height: '450px'
                },
            }}
        >
            <DialogTitle id="secret-variable-dialog-title"
                sx={{
                    m: 0,
                    pt: 1.5,
                    pb: 1,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box>
                    <Typography variant='h6' component='p'>{initialFormData.name ? "Edit" : "Add"} {headingTitle}</Typography>
                </Box>

                <IconButton
                    aria-label="close"
                    type='button'
                    onClick={handleClose}
                    sx={{ width: 45, height: 45 }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant='body2' mb={2}>{headingTitle}s will be passed to certion actions and cannot be read otherwise.</Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Controller
                            name="name"
                            control={control}
                            rules={{
                                required: 'Name is required',
                                minLength: { value: 2, message: 'Name must be at least 2 characters' },
                                maxLength: { value: 30, message: 'Name must be at most 30 characters' },
                                pattern: {
                                    value: /^[a-zA-Z0-9_]+$/i,
                                    message: 'Only alphanumeric characters or underscores are allowed',
                                },
                                validate: {
                                    notReservedPrefix: (value) =>
                                        !/^gitea_|^github_/i.test(value) || `Invalid ${headingTitle.toLowerCase()} name.`,
                                    initializedSecretsValidator: (value) =>
                                        !initializedSecrets.includes(value) || `'${value}' is a reserved ${headingTitle.toLowerCase()} and cannot be used.`,
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Name"
                                    placeholder='case-insensitive, alphanumeric characters or underscores only'
                                    variant="outlined"
                                    onChange={e => field.onChange(e.target.value.toUpperCase())}
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                    autoComplete='off'
                                    InputProps={{
                                        readOnly: !!initialFormData.name,
                                    }}
                                    fullWidth
                                    autoFocus
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Controller
                            name="value"
                            control={control}
                            rules={{
                                required: 'Value is required',
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Value"
                                    placeholder='Input any content. Whitespace at the start and end will be omitted.'
                                    variant="outlined"
                                    error={!!errors.value}
                                    helperText={errors.value?.message}
                                    multiline
                                    rows={5}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button type='button' variant="outlined" onClick={handleClose} sx={{ width: 120 }}>
                    cancel
                </Button>

                <Button type='submit' variant="contained" sx={{ width: 120 }} disableElevation>
                    confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default SecretAndVariableModal